import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import {
  FileSignature,
  Download,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * "My Documents" — read-only employee view of every document they were
 * asked to sign (pending / signed / declined).
 *
 * Backed by `/api/me/documents` which matches by `employee_id` (primary)
 * and `email` (fallback). Active employees see their signed offer letter
 * and any other docs; non-active employees see the same data but cannot
 * use other modules.
 */
export default function MyDocuments() {
  const [docs, setDocs] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('hrms_token');
        const res = await axios.get(`${API}/me/documents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDocs(res.data?.documents || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load documents');
        setDocs([]);
      }
    };
    load();
  }, []);

  const statusBadge = (myStatus, docStatus) => {
    if (myStatus === 'signed' || docStatus === 'completed') {
      return { Icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', label: 'Signed' };
    }
    if (myStatus === 'declined' || docStatus === 'declined') {
      return { Icon: XCircle, cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', label: 'Declined' };
    }
    return { Icon: Clock, cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', label: 'Awaiting your signature' };
  };

  const handleDownload = async (doc) => {
    const path = doc.signed_pdf_url || doc.file_url;
    if (!path) {
      toast.error('No file is attached to this document');
      return;
    }
    setDownloadingId(doc.document_id);
    try {
      const token = localStorage.getItem('hrms_token');
      const resp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      const safe = (doc.title || 'document').replace(/[^a-z0-9_\-]/gi, '_');
      a.download = `${safe}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  if (docs === null) {
    return (
      <div className="flex items-center justify-center p-16">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-documents-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My Documents</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Documents you have signed, declined, or are awaiting your signature.
        </p>
      </div>

      {docs.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-12 text-center">
          <FileSignature className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 dark:text-slate-300 font-medium">No documents yet</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            When HR sends you a document for signature it will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map((doc) => {
            const { Icon, cls, label } = statusBadge(doc.my_signature_status, doc.document_status);
            return (
              <div
                key={doc.document_id}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-5 shadow-sm flex items-start justify-between gap-4"
                data-testid={`my-doc-row-${doc.document_id}`}
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <FileSignature className="h-5 w-5 text-slate-500 dark:text-slate-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 dark:text-slate-100 truncate">{doc.title}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                        <Icon className="h-3 w-3" />
                        {label}
                      </span>
                      {doc.document_type && (
                        <span className="text-xs text-slate-500">
                          {doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                      {doc.sent_at && (
                        <p>Sent: {new Date(doc.sent_at).toLocaleString()}</p>
                      )}
                      {doc.signed_at && (
                        <p>Signed: {new Date(doc.signed_at).toLocaleString()}</p>
                      )}
                      {doc.declined_at && (
                        <>
                          <p>Declined: {new Date(doc.declined_at).toLocaleString()}</p>
                          {doc.decline_reason && (
                            <p className="italic">Reason: "{doc.decline_reason}"</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  {doc.sign_url && (
                    <Button
                      size="sm"
                      onClick={() => navigate(doc.sign_url)}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid={`open-sign-${doc.document_id}`}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Sign now
                    </Button>
                  )}
                  {(doc.signed_pdf_url || doc.file_url) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(doc)}
                      disabled={downloadingId === doc.document_id}
                      className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-200 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40"
                      data-testid={`download-my-doc-${doc.document_id}`}
                    >
                      {downloadingId === doc.document_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-1" />
                      )}
                      Download
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
